import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { FolderOpen, Plus, Copy, Trash2, Pencil, Check, X, ChevronDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  getAnalyses, createAnalysis, updateAnalysis, deleteAnalysis, duplicateAnalysis,
} from "@/lib/api";

export default function AnalysisManager({ activeId, onChange }) {
  const [analyses, setAnalyses] = useState([]);
  const [showNew, setShowNew] = useState(false);
  const [newName, setNewName] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [editName, setEditName] = useState("");

  const fetchAnalyses = useCallback(async () => {
    try {
      const res = await getAnalyses();
      const list = res.data.analyses || [];
      setAnalyses(list);
      // If activeId doesn't match any analysis, switch to the first one
      if (list.length > 0) {
        const match = list.find((a) => a.id === activeId);
        if (!match) {
          onChange(list[0].id, list[0]);
        }
      }
    } catch (e) {
      console.error(e);
    }
  }, [activeId, onChange]);

  useEffect(() => { fetchAnalyses(); }, [fetchAnalyses]);

  const active = analyses.find((a) => a.id === activeId) || analyses[0];

  const handleCreate = async () => {
    if (!newName.trim()) return;
    try {
      const res = await createAnalysis({ name: newName.trim() });
      setNewName("");
      setShowNew(false);
      fetchAnalyses();
      onChange(res.data.id, res.data);
      toast.success("Analysis created");
    } catch (e) {
      toast.error("Failed to create");
    }
  };

  const handleDuplicate = async (id) => {
    try {
      const res = await duplicateAnalysis(id);
      fetchAnalyses();
      onChange(res.data.id, res.data);
      toast.success("Analysis duplicated");
    } catch (e) {
      toast.error("Failed to duplicate");
    }
  };

  const handleDelete = async (id) => {
    if (analyses.length <= 1) {
      toast.error("Cannot delete the last analysis");
      return;
    }
    try {
      await deleteAnalysis(id);
      const remaining = analyses.filter((a) => a.id !== id);
      if (activeId === id && remaining.length > 0) {
        onChange(remaining[0].id);
      }
      fetchAnalyses();
      toast.success("Analysis deleted");
    } catch (e) {
      toast.error("Failed to delete");
    }
  };

  const handleRename = async (id) => {
    if (!editName.trim()) return;
    try {
      await updateAnalysis(id, { name: editName.trim() });
      setEditingId(null);
      fetchAnalyses();
    } catch (e) {
      toast.error("Failed to rename");
    }
  };

  return (
    <div className="flex items-center gap-2">
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            data-testid="analysis-selector-btn"
            variant="ghost"
            className="text-white hover:bg-white/10 gap-2 max-w-[250px]"
          >
            <FolderOpen className="w-4 h-4 flex-shrink-0" />
            <span className="truncate text-sm font-medium">{active?.name || "Select Analysis"}</span>
            <ChevronDown className="w-3 h-3 flex-shrink-0 opacity-60" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-72">
          {analyses.map((a) => (
            <DropdownMenuItem
              key={a.id}
              data-testid={`analysis-item-${a.id}`}
              className={`flex items-center justify-between cursor-pointer ${a.id === activeId ? "bg-[#F1EFEA]" : ""}`}
              onSelect={(e) => {
                if (editingId === a.id) { e.preventDefault(); return; }
                onChange(a.id, a);
              }}
            >
              {editingId === a.id ? (
                <div className="flex items-center gap-1 w-full" onClick={(e) => e.stopPropagation()}>
                  <Input
                    value={editName}
                    onChange={(e) => setEditName(e.target.value)}
                    className="h-7 text-xs"
                    autoFocus
                    onKeyDown={(e) => { if (e.key === "Enter") handleRename(a.id); }}
                  />
                  <button onClick={() => handleRename(a.id)} className="p-1 text-[#2D4A3E]"><Check className="w-3 h-3" /></button>
                  <button onClick={() => setEditingId(null)} className="p-1 text-[#78716C]"><X className="w-3 h-3" /></button>
                </div>
              ) : (
                <>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">{a.name}</p>
                    <p className="text-xs text-[#78716C]">{a.transaction_count || 0} tx / {a.offer_count || 0} offers</p>
                  </div>
                  <div className="flex items-center gap-0.5 ml-2" onClick={(e) => e.stopPropagation()}>
                    <button
                      data-testid={`rename-analysis-${a.id}`}
                      onClick={() => { setEditingId(a.id); setEditName(a.name); }}
                      className="p-1 text-[#78716C] hover:text-[#1C1917]"
                    ><Pencil className="w-3 h-3" /></button>
                    <button
                      data-testid={`duplicate-analysis-${a.id}`}
                      onClick={() => handleDuplicate(a.id)}
                      className="p-1 text-[#78716C] hover:text-[#1C1917]"
                    ><Copy className="w-3 h-3" /></button>
                    <button
                      data-testid={`delete-analysis-${a.id}`}
                      onClick={() => handleDelete(a.id)}
                      className="p-1 text-[#78716C] hover:text-[#E07A5F]"
                    ><Trash2 className="w-3 h-3" /></button>
                  </div>
                </>
              )}
            </DropdownMenuItem>
          ))}
          <DropdownMenuSeparator />
          <DropdownMenuItem
            data-testid="new-analysis-btn"
            onSelect={(e) => { e.preventDefault(); setShowNew(true); }}
            className="cursor-pointer"
          >
            <Plus className="w-4 h-4 mr-2" />
            New Analysis
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <Dialog open={showNew} onOpenChange={setShowNew}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-['Playfair_Display']">New Analysis</DialogTitle>
            <DialogDescription>Create a new loan analysis workspace.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <Input
              data-testid="new-analysis-name-input"
              placeholder="e.g. ABC Corp - Q1 2024"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") handleCreate(); }}
              autoFocus
            />
            <Button data-testid="create-analysis-btn" onClick={handleCreate} className="w-full bg-[#1A362D] hover:bg-[#264B40]">
              Create Analysis
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
